import React, { useState, useRef, useEffect, useCallback } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { Icon, Style, Fill, Stroke, Circle } from 'ol/style';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import 'ol/ol.css';
import './SplitMap.css';
import { MapStyle, noneMapStyle } from './MapStyleDialog';

function latLngToTileCoords(lat: number, lng: number, zoom: number) {
  const x = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
  const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
  return { x, y };
}

function tileCoordsToLatLng(x: number, y: number, zoom: number) {
  const n = Math.pow(2, zoom);
  const lng = x / n * 360 - 180;
  const lat = Math.atan(Math.sinh(Math.PI * (1 - 2 * y / n))) * 180 / Math.PI;
  return { lat, lng };
}

function updateLocationHash(zoom: number, lat: number, lng: number) {
  const hash = window.location.hash.slice(1);
  const [, queryPart] = hash.split('?');
  const locationPart = `${zoom}/${lat.toFixed(6)}/${lng.toFixed(6)}`;
  const newHash = queryPart ? `${locationPart}?${queryPart}` : locationPart;
  window.location.hash = newHash;
}

interface SplitMapProps {
  searchResult?: {
    lat: number;
    lon: number;
    zoom?: number;
    display_name: string;
  };
  leftMapStyle: MapStyle;
  rightMapStyle: MapStyle;
  rightBaseMapStyle: MapStyle;
  rightThirdMapStyle: MapStyle;
}

const SplitMap: React.FC<SplitMapProps> = ({ searchResult, leftMapStyle, rightMapStyle, rightBaseMapStyle, rightThirdMapStyle }) => {
  const parseHash = useCallback(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const [locationPart] = hash.split('?');
      if (locationPart) {
        const parts = locationPart.split('/');
        if (parts.length === 3) {
          const zoom = parseInt(parts[0], 10);
          const lat = parseFloat(parts[1]);
          const lon = parseFloat(parts[2]);
          if (!isNaN(zoom) && !isNaN(lat) && !isNaN(lon)) {
            return { zoom, center: [lon, lat] as Coordinate };
          }
        }
      }
    }
    return { zoom: 13, center: [-0.09, 51.505] as Coordinate };
  }, []);

  const initialState = parseHash();
  const [syncedCenter, setSyncedCenter] = useState<Coordinate>(initialState.center);
  const [syncedZoom, setSyncedZoom] = useState(initialState.zoom);
  const [activeMap, setActiveMap] = useState<'left' | 'right'>('left');
  const activeMapRef = useRef<'left' | 'right'>('left');
  const [syncedCursor, setSyncedCursor] = useState<Coordinate | null>(null);

  const leftMapRef = useRef<HTMLDivElement>(null);
  const rightMapRef = useRef<HTMLDivElement>(null);
  const leftOlMapRef = useRef<Map | null>(null);
  const rightOlMapRef = useRef<Map | null>(null);
  
  const leftTileLayerRef = useRef<TileLayer<XYZ> | null>(null);
  const rightBaseTileLayerRef = useRef<TileLayer<XYZ> | null>(null);
  const rightTopTileLayerRef = useRef<TileLayer<XYZ> | null>(null);
  const rightThirdTileLayerRef = useRef<TileLayer<XYZ> | null>(null);

  const leftMarkerLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const rightMarkerLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const leftCursorLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const rightCursorLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const leftTileBoundaryLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const rightTileBoundaryLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  
  const leftCurrentTileRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  const rightCurrentTileRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  const isUpdatingViewRef = useRef(false);
  const lastSyncedPositionRef = useRef<{ center: Coordinate; zoom: number } | null>(null);

  const handleMapMove = useCallback((center: Coordinate, zoom: number, sourceMap: Map) => {
    // Update the other map directly without going through state
    const targetMap = sourceMap === leftOlMapRef.current ? rightOlMapRef.current : leftOlMapRef.current;
    
    if (targetMap) {
      isUpdatingViewRef.current = true;
      const centerOL = fromLonLat(center);
      targetMap.getView().setCenter(centerOL);
      targetMap.getView().setZoom(zoom);
      
      setTimeout(() => {
        isUpdatingViewRef.current = false;
      }, 50);
    }
    
    // Update state for external consumers (hash, etc.)
    setSyncedCenter(center);
    setSyncedZoom(zoom);
  }, []);

  const handleCursorMove = useCallback((coordinate: Coordinate | null) => {
    setSyncedCursor(coordinate);
  }, []);

  const createMarkerStyle = () => new Style({
    image: new Icon({
      anchor: [0.5, 1],
      src: 'data:image/svg+xml;base64,' + btoa(`
        <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 0C5.6 0 0 5.6 0 12.5S12.5 41 12.5 41 25 19.4 25 12.5 19.4 0 12.5 0z" fill="#3b82f6"/>
          <circle cx="12.5" cy="12.5" r="6" fill="white"/>
        </svg>
      `),
    }),
  });

  const createCursorStyle = () => new Style({
    image: new Circle({
      radius: 12,
      fill: new Fill({
        color: 'rgba(59, 130, 246, 0.3)',
      }),
      stroke: new Stroke({
        color: '#1e40af',
        width: 3,
      }),
    }),
  });

  const createTileBoundaryStyle = () => new Style({
    stroke: new Stroke({
      color: '#ff7800',
      width: 2,
    }),
    fill: new Fill({
      color: 'rgba(255, 120, 0, 0.1)',
    }),
  });

  const createMap = (
    target: HTMLDivElement,
    mapStyle: MapStyle,
    isLeft: boolean,
    baseMapStyle?: MapStyle,
    thirdMapStyle?: MapStyle
  ) => {
    const tileLayers: TileLayer<XYZ>[] = [];

    if (isLeft) {
      const tileLayer = new TileLayer({
        source: new XYZ({
          url: mapStyle.url,
          attributions: mapStyle.attribution,
        }),
      });
      leftTileLayerRef.current = tileLayer;
      tileLayers.push(tileLayer);
    } else {
      const baseTileLayer = new TileLayer({
        source: new XYZ({
          url: baseMapStyle!.url,
          attributions: baseMapStyle!.attribution,
        }),
      });
      const topTileLayer = new TileLayer({
        source: new XYZ({
          url: mapStyle.url,
          attributions: mapStyle.attribution,
        }),
      });
      const thirdIsNone = thirdMapStyle!.name === noneMapStyle.name;
      const thirdTileLayer = new TileLayer({
        visible: !thirdIsNone,
        source: thirdIsNone ? undefined : new XYZ({
          url: thirdMapStyle!.url,
          attributions: thirdMapStyle!.attribution,
        }),
      });
      rightBaseTileLayerRef.current = baseTileLayer;
      rightTopTileLayerRef.current = topTileLayer;
      rightThirdTileLayerRef.current = thirdTileLayer;
      tileLayers.push(baseTileLayer, topTileLayer, thirdTileLayer);
    }

    const markerLayer = new VectorLayer({
      source: new VectorSource(),
      style: createMarkerStyle(),
    });

    const cursorLayer = new VectorLayer({
      source: new VectorSource(),
      style: createCursorStyle(),
    });

    const tileBoundaryLayer = new VectorLayer({
      source: new VectorSource(),
      style: createTileBoundaryStyle(),
    });

    if (isLeft) {
      leftMarkerLayerRef.current = markerLayer;
      leftCursorLayerRef.current = cursorLayer;
      leftTileBoundaryLayerRef.current = tileBoundaryLayer;
    } else {
      rightMarkerLayerRef.current = markerLayer;
      rightCursorLayerRef.current = cursorLayer;
      rightTileBoundaryLayerRef.current = tileBoundaryLayer;
    }

    const map = new Map({
      target,
      layers: [...tileLayers, markerLayer, cursorLayer, tileBoundaryLayer],
      view: new View({
        center: fromLonLat(syncedCenter),
        zoom: syncedZoom,
        constrainResolution: true,
        maxZoom: 19,
      }),
      controls: [],
    });

    map.on('moveend', () => {
      if (isUpdatingViewRef.current) {
        return;
      }
      
      const view = map.getView();
      const mapCenter = toLonLat(view.getCenter()!);
      const mapZoom = view.getZoom()!;
      const newCenter: Coordinate = [mapCenter[0], mapCenter[1]];
      
      // Check if position actually changed to prevent unnecessary updates
      const lastSynced = lastSyncedPositionRef.current;
      if (lastSynced && 
          Math.abs(lastSynced.center[0] - newCenter[0]) < 0.000001 &&
          Math.abs(lastSynced.center[1] - newCenter[1]) < 0.000001 &&
          lastSynced.zoom === mapZoom) {
        return;
      }
      
      lastSyncedPositionRef.current = { center: newCenter, zoom: mapZoom };
      handleMapMove(newCenter, mapZoom, map);
      updateLocationHash(mapZoom, mapCenter[1], mapCenter[0]);
    });

    map.on('pointermove', (evt) => {
      if (activeMapRef.current === (isLeft ? 'left' : 'right')) {
        const coordinate = evt.coordinate;
        handleCursorMove(coordinate);

        const lonLat = toLonLat(coordinate);
        const mapZoom = map.getView().getZoom()!;
        const tileCoords = latLngToTileCoords(lonLat[1], lonLat[0], mapZoom);
        
        const currentTileRef = isLeft ? leftCurrentTileRef : rightCurrentTileRef;
        const current = currentTileRef.current;
        if (current && current.x === tileCoords.x && current.y === tileCoords.y && current.zoom === mapZoom) {
          return;
        }
        
        currentTileRef.current = { x: tileCoords.x, y: tileCoords.y, zoom: mapZoom };
        
        const topLeft = tileCoordsToLatLng(tileCoords.x, tileCoords.y, mapZoom);
        const bottomRight = tileCoordsToLatLng(tileCoords.x + 1, tileCoords.y + 1, mapZoom);
        
        const polygon = new Polygon([[
          fromLonLat([topLeft.lng, topLeft.lat]),
          fromLonLat([bottomRight.lng, topLeft.lat]),
          fromLonLat([bottomRight.lng, bottomRight.lat]),
          fromLonLat([topLeft.lng, bottomRight.lat]),
          fromLonLat([topLeft.lng, topLeft.lat])
        ]]);
        
        const feature = new Feature(polygon);
        
        const tileBoundaryLayerRef = isLeft ? leftTileBoundaryLayerRef : rightTileBoundaryLayerRef;
        if (tileBoundaryLayerRef.current) {
          tileBoundaryLayerRef.current.getSource()!.clear();
          tileBoundaryLayerRef.current.getSource()!.addFeature(feature);
        }
      }
    });

    map.getViewport().addEventListener('mouseout', () => {
      if (activeMapRef.current === (isLeft ? 'left' : 'right')) {
        handleCursorMove(null);
        const currentTileRef = isLeft ? leftCurrentTileRef : rightCurrentTileRef;
        const tileBoundaryLayerRef = isLeft ? leftTileBoundaryLayerRef : rightTileBoundaryLayerRef;
        currentTileRef.current = null;
        if (tileBoundaryLayerRef.current) {
          tileBoundaryLayerRef.current.getSource()!.clear();
        }
      }
    });

    return map;
  };

  useEffect(() => {
    if (leftMapRef.current && !leftOlMapRef.current) {
      leftOlMapRef.current = createMap(leftMapRef.current, leftMapStyle, true);
    }
    if (rightMapRef.current && !rightOlMapRef.current) {
      rightOlMapRef.current = createMap(rightMapRef.current, rightMapStyle, false, rightBaseMapStyle, rightThirdMapStyle);
    }

    return () => {
      if (leftOlMapRef.current) {
        leftOlMapRef.current.setTarget(undefined);
        leftOlMapRef.current = null;
      }
      if (rightOlMapRef.current) {
        rightOlMapRef.current.setTarget(undefined);
        rightOlMapRef.current = null;
      }
    };
  }, []); // Only create maps once

  // Handle style changes separately
  useEffect(() => {
    if (leftTileLayerRef.current) {
      const newSource = new XYZ({
        url: leftMapStyle.url,
        attributions: leftMapStyle.attribution,
      });
      leftTileLayerRef.current.setSource(newSource);
    }
  }, [leftMapStyle]);

  useEffect(() => {
    if (rightTopTileLayerRef.current) {
      const newSource = new XYZ({
        url: rightMapStyle.url,
        attributions: rightMapStyle.attribution,
      });
      rightTopTileLayerRef.current.setSource(newSource);
    }
  }, [rightMapStyle]);

  useEffect(() => {
    if (rightBaseTileLayerRef.current) {
      const newSource = new XYZ({
        url: rightBaseMapStyle.url,
        attributions: rightBaseMapStyle.attribution,
      });
      rightBaseTileLayerRef.current.setSource(newSource);
    }
  }, [rightBaseMapStyle]);

  useEffect(() => {
    const layer = rightThirdTileLayerRef.current;
    if (!layer) return;
    if (rightThirdMapStyle.name === noneMapStyle.name) {
      layer.setVisible(false);
      layer.setSource(null);
      return;
    }
    layer.setSource(new XYZ({
      url: rightThirdMapStyle.url,
      attributions: rightThirdMapStyle.attribution,
    }));
    layer.setVisible(true);
  }, [rightThirdMapStyle]);

  // Sync both maps only for external updates (search results, hash changes)
  const syncBothMaps = useCallback((center: Coordinate, zoom: number) => {
    isUpdatingViewRef.current = true;
    
    const centerOL = fromLonLat(center);
    [leftOlMapRef.current, rightOlMapRef.current].forEach(map => {
      if (map) {
        map.getView().setCenter(centerOL);
        map.getView().setZoom(zoom);
      }
    });
    
    setTimeout(() => {
      isUpdatingViewRef.current = false;
    }, 100);
  }, []);

  useEffect(() => {
    if (syncedCursor) {
      const inactiveMap = activeMap === 'left' ? rightOlMapRef.current : leftOlMapRef.current;
      const inactiveCursorLayer = activeMap === 'left' ? rightCursorLayerRef.current : leftCursorLayerRef.current;
      
      if (inactiveMap && inactiveCursorLayer) {
        const feature = new Feature({
          geometry: new Point(syncedCursor),
        });
        
        inactiveCursorLayer.getSource()!.clear();
        inactiveCursorLayer.getSource()!.addFeature(feature);
      }
    } else {
      [leftCursorLayerRef.current, rightCursorLayerRef.current].forEach(layer => {
        if (layer) {
          layer.getSource()!.clear();
        }
      });
    }
  }, [syncedCursor, activeMap]);

  useEffect(() => {
    if (searchResult) {
      const newCenter = [searchResult.lon, searchResult.lat] as Coordinate;
      const newZoom = searchResult.zoom ?? 16;
      lastSyncedPositionRef.current = { center: newCenter, zoom: newZoom };

      // Sync both maps for search results
      syncBothMaps(newCenter, newZoom);

      // Update state for external consumers
      setSyncedCenter(newCenter);
      setSyncedZoom(newZoom);

      updateLocationHash(newZoom, searchResult.lat, searchResult.lon);
    }
  }, [searchResult, syncBothMaps]);

  useEffect(() => {
    const handleHashChange = () => {
      const newState = parseHash();
      lastSyncedPositionRef.current = { center: newState.center, zoom: newState.zoom };
      
      // Sync both maps for hash changes
      syncBothMaps(newState.center, newState.zoom);
      
      // Update state for external consumers
      setSyncedCenter(newState.center);
      setSyncedZoom(newState.zoom);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [parseHash, syncBothMaps]);

  return (
    <div className="split-map-container">
      <div className="map-pane" onMouseEnter={() => { activeMapRef.current = 'left'; setActiveMap('left'); }}>
        <div className="map-label">Left</div>
        <div ref={leftMapRef} style={{ height: '100%', width: '100%' }} />
      </div>

      <div className="map-pane" onMouseEnter={() => { activeMapRef.current = 'right'; setActiveMap('right'); }}>
        <div className="map-label">Right</div>
        <div ref={rightMapRef} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default SplitMap;